
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

interface MobilePrudentProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    activeFilters: string[];
    toggleFilter: (filter: string) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const MobilePrudent: React.FC<MobilePrudentProps> = ({
    data,
    activeTab,
    setActiveTab,
    searchTerm,
    setSearchTerm,
    activeFilters,
    toggleFilter,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '16px', maxWidth: '100vw', overflowX: 'hidden' }}>
            <Disclaimer type="investment" />

            {/* Mobile Summary Grid */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>🐢 Opportunités</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Marché</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                            fontSize: '11px',
                            padding: '2px 8px'
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Scrollable Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Achats ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Ventes ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Stratégie
                </button>
            </div>

            {/* Opportunities (Cards) */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>
                                <p style={{ fontSize: '14px' }}>Aucune zone de survente (Stoch {'<'} 20).</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${opp.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{opp.symbole}</span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={styles.scoreContainer}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: '#16a34a' }}>
                                                {opp.score}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Prix</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Stoch %K</span>
                                            <span style={{ ...styles.priceValue, color: '#16a34a' }}>{opp.stoch_k?.toFixed(0)}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>RSI</span>
                                            <span style={{ ...styles.priceValue, color: (opp.rsi || 50) < 30 ? '#16a34a' : 'inherit' }}>
                                                {opp.rsi?.toFixed(0)}
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '14px' }}>Aucune alerte de surachat.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal: any) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${signal.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{signal.symbole}</span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <span style={{ ...styles.badge, backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', fontSize: '10px' }}>
                                            ALERTE
                                        </span>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : '#dc2626' }}>
                                                {signal.unrealized_pnl_pct?.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: '#dc2626' }}>
                                            {signal.recommended_action === 'SELL_ALL' ? 'VENDRE TOUT' : 'ALLÉGER'}
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Universe (Cards on mobile) */}
            {activeTab === 'universe' && (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <input
                            type="text"
                            placeholder="🔍 Chercher une action..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
                            <button
                                onClick={() => toggleFilter('stoch20')}
                                style={{ ...styles.filterChip, ...(activeFilters.includes('stoch20') ? styles.filterChipActive : {}) }}
                            >
                                Stoch {'<'} 20
                            </button>
                            <button
                                onClick={() => toggleFilter('undervalued')}
                                style={{ ...styles.filterChip, ...(activeFilters.includes('undervalued') ? styles.filterChipActive : {}) }}
                            >
                                💎 Sous-éval
                            </button>
                            <button
                                onClick={() => toggleFilter('liquid')}
                                style={{ ...styles.filterChip, ...(activeFilters.includes('liquid') ? styles.filterChipActive : {}) }}
                            >
                                💧 Liquide
                            </button>
                        </div>
                    </div>

                    <div style={styles.cardsGrid}>
                        {filteredUniverse.slice(0, 20).map((stock: any) => (
                            <Box key={stock.symbole}>
                                <div style={{ 
                                    ...styles.card, 
                                    borderLeft: stock.is_opportunity ? '4px solid #16a34a' : '1px solid var(--border-color)' 
                                }} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{stock.symbole}</span>
                                            {stock.is_opportunity && <span style={{ marginLeft: 6, fontSize: '10px' }}>🐢</span>}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{stock.cours_actuel?.toLocaleString()}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 15, marginTop: 8, fontSize: '12px', color: 'var(--text-muted)' }}>
                                        <span>%K: <b style={{ color: stock.stoch_k < 20 ? '#16a34a' : 'inherit' }}>{stock.stoch_k?.toFixed(0)}</b></span>
                                        <span>RSI: {stock.rsi?.toFixed(0)}</span>
                                        <span>GC: {stock.is_golden_cross ? '🏆' : '-'}</span>
                                    </div>
                                </div>
                            </Box>
                        ))}
                    </div>
                </div>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 16 }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>🐢 Stratégie "Investisseur Prudent"</h3>
                        <div style={{ fontSize: '13px', lineHeight: '1.5', color: 'var(--text-main)' }}>
                            <p>Identification de points de retournement par l'oscillateur Stochastique.</p>
                            
                            <h4 style={{ marginTop: 15, fontSize: '14px', color: '#16a34a' }}>✅ Achat</h4>
                            <p>Stochastique %K {'<'} 20 (Survente) + RSI {'<'} 70.</p>

                            <h4 style={{ marginTop: 15, fontSize: '14px', color: '#dc2626' }}>❌ Vente</h4>
                            <p>Stochastique %K {'>'} 80 (Surachat) ou Stop Loss.</p>
                        </div>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px' },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: '11px', color: 'var(--text-muted)', marginBottom: 2 },
    summaryValue: { fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 10px', borderRadius: 20, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none' },
    tab: { padding: '8px 14px', border: 'none', borderRadius: 20, fontSize: '12px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)', whiteSpace: 'nowrap' },
    tabActive: { backgroundColor: '#16a34a', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveInfo: { backgroundColor: '#2563eb', color: 'white' },
    cardsGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: { padding: '14px', backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
    symbolLink: { fontSize: '16px', fontWeight: 'bold', color: '#2563eb' },
    companyName: { fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0 0' },
    scoreContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    scoreBadge: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12, padding: '10px', backgroundColor: 'var(--bg-input)', borderRadius: 8 },
    priceItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    priceLabel: { fontSize: '9px', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' },
    priceValue: { fontSize: '12px', fontWeight: 'bold', color: 'var(--text-heading)' },
    justification: { fontSize: '12px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic', lineHeight: '1.4' },
    actionRow: { display: 'flex', justifyContent: 'center', marginTop: 12 },
    actionBadge: { padding: '6px 16px', borderRadius: 6, fontSize: '11px', fontWeight: 'bold', color: 'white' },
    searchInput: { width: '100%', padding: '10px 15px', borderRadius: 20, border: '1px solid var(--border-color)', fontSize: '14px', outline: 'none', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)' },
    filterChip: { padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    filterChipActive: { backgroundColor: 'rgba(37, 99, 235, 0.1)', borderColor: '#2563eb', color: '#2563eb', fontWeight: 'bold' },
};

export default MobilePrudent;
